import { useCurrentOrganizationId } from '@cast/infrastructure-organization';

import { useMemo } from 'react';

import dayjs from 'dayjs';

import { useDboParams } from 'features/db-optimization/v2/hooks/useDboParams';
import { useDboTimeRangeSelection } from 'features/db-optimization/hooks/useDboTimeRangeSelection';
import { useDatabaseInstanceDetailsQuery } from 'hooks/queries/dbo/useDatabaseInstanceDetails';
import { toInstanceModelV2 } from '../utils/instanceToViewV2';

type UseInstanceDetailsOptions = {
  /** Ignore the URL time range and fetch a fixed last-hour window. */
  lastHourOnly?: boolean;
};

export const useInstanceDetails = ({
  lastHourOnly = false,
}: UseInstanceDetailsOptions = {}) => {
  const organizationId = useCurrentOrganizationId();
  const { dbServerId } = useDboParams();
  const { range } = useDboTimeRangeSelection();

  const effectiveRange = useMemo(() => {
    if (!lastHourOnly) {
      return range;
    }

    const to = dayjs().startOf('minute');
    return {
      from: to.subtract(1, 'hour').toISOString(),
      to: to.toISOString(),
    };
  }, [lastHourOnly, range]);

  return useDatabaseInstanceDetailsQuery(
    {
      organizationId: organizationId ?? '',
      id: dbServerId!,
      startTime: effectiveRange.from,
      endTime: effectiveRange.to,
    },
    {
      enabled: !!organizationId && !!dbServerId,
      select: toInstanceModelV2,
    }
  );
};
